import type { MapPlotOptions } from '../../../PageConfigProvider/types/item';
import type { MetricValues } from '../../../PageConfigProvider/types/metric';
import { LocationType } from '../types';
import { countryNameToIsoCode } from './country-codes';
import { getSurfaceAreaById, SurfaceAreas } from './surface-areas';

export function normalizePointValue(
  rawValue: number,
  _metric: MetricValues | undefined,
  locationType: LocationType,
  locationId: string,
  mapOptions?: MapPlotOptions,
  locationLabel?: string
): { value: number | null; displayValue: number } {
  const displayValue = rawValue;

  const normalization = mapOptions?.normalization ?? 'none';
  const transformation = mapOptions?.transformation ?? 'none';

  let value = rawValue;

  if (normalization === 'area_density') {
    const area = getSurfaceAreaForLocation(
      locationType,
      locationId,
      locationLabel
    );

    if (area === null) {
      return { value: null, displayValue };
    }

    value = (value / Math.sqrt(area)) * 1_000;
  }

  if (transformation === 'log' && value >= 0) {
    value = Math.log1p(value);
  }

  if (transformation === 'sqrt' && value >= 0) {
    value = Math.sqrt(value);
  }

  return { value, displayValue };
}

function normalizeLabelForArea(raw?: string): string {
  if (!raw) return '';
  let s = (raw || '').trim().toLowerCase();
  s = s.replace(/\s*non[- ]?metro$/i, '').trim();
  s = s.replace(/,\s*united states$/i, '').trim();
  if (s === 'united states of america' || s === 'usa') s = 'united states';
  return s;
}

function getSurfaceAreaForLocation(
  locationType: LocationType,
  locationId: string,
  locationLabel?: string
): number | null {
  if (
    locationType === LocationType.METRO_AREA ||
    locationType === LocationType.STATE ||
    locationType === LocationType.COUNTY ||
    locationType === LocationType.CITY
  ) {
    return getSurfaceAreaById(SurfaceAreas[locationType], locationId);
  }

  const store = SurfaceAreas[locationType];
  const key = normalizeLabelForArea(locationLabel);
  if (!key) return null;

  const area =
    locationType === LocationType.COUNTRY
      ? store[countryNameToIsoCode[key] ?? '']
      : store[key];
  return Number.isFinite(area) && (area as number) > 0
    ? (area as number)
    : null;
}
